import { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Paper,
  TableContainer, MenuItem, Select, InputLabel, FormControl, OutlinedInput, Chip,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import {
  getContratos, createContrato, updateContrato, deleteContrato,
  getRestaurantes, getProdutos,
} from '../services/api';

const STATUS_OPTIONS = ['ativo', 'cancelado', 'inadimplente', 'trial'];

const formatCurrency = (value) => {
  if (!value) return 'R$ 0,00';
  return `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}`;
};

const ContratosPage = () => {
  const [contratos, setContratos] = useState([]);
  const [restaurantes, setRestaurantes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    restaurante_id: '',
    valor_mensal: '',
    dia_vencimento: '',
    data_inicio: '',
    data_fim: '',
    status: 'ativo',
    observacoes: '',
    produto_ids: [],
    nota_fiscal: false,
    envio_nota_fiscal: '',
    forma_pagamento: '',
  });

  const restauranteMap = new Map(restaurantes.map((r) => [r.id, r.nome]));
  const produtoMap = new Map(produtos.map((p) => [p.id, p.nome]));

  const load = async () => {
    const [contratosData, restaurantesData, produtosData] = await Promise.all([
      getContratos(),
      getRestaurantes(),
      getProdutos(),
    ]);
    setContratos(contratosData);
    setRestaurantes(restaurantesData);
    setProdutos(produtosData);
  };

  useEffect(() => { load(); }, []);

  const handleOpen = (contrato = null) => {
    setEditing(contrato);
    if (contrato) {
      setForm({
        ...contrato,
        data_inicio: contrato.data_inicio || '',
        data_fim: contrato.data_fim || '',
        produto_ids: contrato.financeiro_contrato_produtos.map(p => p.produto_id) || [],
        nota_fiscal: contrato.nota_fiscal || false,
        envio_nota_fiscal: contrato.envio_nota_fiscal || '',
        forma_pagamento: contrato.forma_pagamento || '',
      });
    } else {
      setForm({
        restaurante_id: '', valor_mensal: '', dia_vencimento: '', data_inicio: '',
        data_fim: '', status: 'ativo', observacoes: '', produto_ids: [],
        nota_fiscal: false,
        envio_nota_fiscal: '',
        forma_pagamento: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    const payload = { ...form };
    if (editing) await updateContrato(editing.id, payload);
    else await createContrato(payload);
    handleClose();
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir contrato?')) return;
    await deleteContrato(id);
    load();
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  
  const handleProductChange = (event) => {
    const { target: { value } } = event;
    if (value.includes('all')) {
      const allProductIds = form.produto_ids.length === produtos.length ? [] : produtos.map(p => p.id);
      setForm({ ...form, produto_ids: allProductIds });
    } else {
      setForm({ ...form, produto_ids: typeof value === 'string' ? value.split(',') : value });
    }
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Contratos</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Novo</Button>
      </Box>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Restaurante</TableCell>
              <TableCell>Valor Mensal</TableCell>
              <TableCell>Dia Vencimento</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contratos.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.id}</TableCell>
                <TableCell>{restauranteMap.get(c.restaurante_id) || c.restaurante_id}</TableCell>
                <TableCell>{formatCurrency(c.valor_mensal)}</TableCell>
                <TableCell>{c.dia_vencimento}</TableCell>
                <TableCell>{c.status}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleOpen(c)} aria-label="Editar contrato"><Edit /></IconButton>
                  <IconButton color="error" onClick={() => handleDelete(c.id)} aria-label="Excluir contrato"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {contratos.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">Nenhum contrato cadastrado.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar Contrato' : 'Novo Contrato'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Restaurante</InputLabel>
            <Select name="restaurante_id" label="Restaurante" value={form.restaurante_id} onChange={handleChange}>
              {restaurantes.map((r) => (<MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Produtos</InputLabel>
            <Select
              multiple
              name="produto_ids"
              value={form.produto_ids}
              onChange={handleProductChange}
              input={<OutlinedInput label="Produtos" />}
              renderValue={(selectedIds) => {
                const hasAll = produtos.length > 0 && selectedIds.length === produtos.length;
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {/* --- LÓGICA DE EXIBIÇÃO NO FORMULÁRIO --- */}
                    {hasAll ? (
                      <Chip label="Plataforma Completa" color="success" />
                    ) : (
                      selectedIds.map((id) => (
                        <Chip key={id} label={produtoMap.get(id)} />
                      ))
                    )}
                  </Box>
                );
              }}
            >
              <MenuItem key="all" value="all">
                <em>{form.produto_ids.length === produtos.length ? 'Desmarcar Todos' : 'Plataforma Completa'}</em>
              </MenuItem>
              {produtos.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField label="Valor Mensal" name="valor_mensal" value={form.valor_mensal} onChange={handleChange} fullWidth />
          <TextField label="Dia Vencimento" name="dia_vencimento" value={form.dia_vencimento} onChange={handleChange} fullWidth />
          <TextField label="Data Início" type="date" name="data_inicio" value={form.data_inicio} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Data Fim" type="date" name="data_fim" value={form.data_fim} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select name="status" label="Status" value={form.status} onChange={handleChange}>
              {STATUS_OPTIONS.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
            </Select>
          </FormControl>
          <TextField label="Observações" name="observacoes" value={form.observacoes} onChange={handleChange} fullWidth multiline rows={3} />
          <FormControl fullWidth>
            <InputLabel>Nota Fiscal</InputLabel>
            <Select
              name="nota_fiscal"
              label="Nota Fiscal"
              value={form.nota_fiscal ? 'true' : 'false'}
              onChange={(e) => setForm({ ...form, nota_fiscal: e.target.value === 'true' })}
            >
              <MenuItem value="true">Sim</MenuItem>
              <MenuItem value="false">Não</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Envio Nota Fiscal" name="envio_nota_fiscal" value={form.envio_nota_fiscal} onChange={handleChange} fullWidth />
          <TextField label="Forma Pagamento" name="forma_pagamento" value={form.forma_pagamento} onChange={handleChange} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContratosPage;